import type { RingFinishData } from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import { WIZARD_PAGE_VIEWED } from '@/app/[locale]/product/_consts';
import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import type { Ring } from '@/app/[locale]/product/_hooks/types';
import type { UseWizardState } from '@/app/[locale]/product/_hooks/useWizardState/types';
import type { RingFinishEnum } from '@/app/enums/RingFinishEnum';
import { type RingStyleEnum } from '@/app/enums/RingStyleEnum';
import useCurrency from '@/app/hooks/useCurrency';
import type { RingFinish, RingStyle } from '@/app/types/ring';
import type { ListProduct } from '@/queries/Products';

import WizStepId from '../enums/WizStepId';
import getRingFinishesData from '../utils/getRingFinishesData';
import sortFinishes from '../utils/sortFinishes';

export interface FinishStepReturn extends UseWizardState {
  active: boolean;
  currencyCode: string;
  isSizesHalfDepleted: boolean;
  or4RingFinishesData: (RingFinishData | undefined)[];
  renderFinishes: Ring[] | undefined;
  selectedFinish: RingFinish | undefined;
  onSelect: (value: RingFinish) => Promise<void>;
}

interface SendProductViewedEventProps {
  pd: ListProduct | undefined;
  stl: RingStyle;
  fnsh: RingFinish;
}

// We need this because we are rendering the wizard twice (mobile + desktop in /product/_components/WizardPageLayout/Layout.tsx) resulting
// in the product viewed event being called twice.  Ideally his event should be lifted out of this, but this will work for now
let productViewCalled = false;

const useFinishStep = (): FinishStepReturn => {
  const wizardContext = useWizardStateContext();
  const { currencyCode } = useCurrency();
  const { isSizesHalfDepletedForFinish } = usePDPContext();
  const { sendProductViewedEvent } = useProductAnalytics();
  const [active, setActive] = useState<boolean>(false);
  const t = useTranslations();

  const currentRoute = wizardContext.currentRoute;
  const filteredFinishByInStock = wizardContext.filteredFinishByInStock;
  const product = wizardContext.product;
  const routeStyle = currentRoute.style;
  const ringsByStyle: Ring[] | undefined = filteredFinishByInStock?.filter(
    ({ style }) => style === routeStyle.value,
  );
  const finish = currentRoute.finish;
  const style = currentRoute.style;
  const isOuraRing4Wizard = currentRoute.isOuraRing4Wizard;

  const renderFinishes = sortFinishes(ringsByStyle);

  const or4RingFinishesData = getRingFinishesData(renderFinishes, t);

  const isSizesHalfDepleted =
    isSizesHalfDepletedForFinish({
      finish: finish.value as RingFinish,
      isOuraRing4: isOuraRing4Wizard,
      style: style.value as RingStyle,
    }) && renderFinishes.length > 0;

  const selectedFinish = finish.value as RingFinishEnum;

  const _sendProductViewedEvent = useCallback(
    async ({ pd, stl, fnsh }: SendProductViewedEventProps) => {
      const path = isOuraRing4Wizard
        ? `product/rings/${stl}/${fnsh}`
        : `product/rings/oura-gen3/${stl}/${fnsh}`;

      await sendProductViewedEvent({
        path,
        product: pd,
      });
    },
    [isOuraRing4Wizard, sendProductViewedEvent],
  );

  const handleSelect = useCallback(
    async (fnsh: RingFinish) => {
      // send track event on selection change
      const _product = ringsByStyle?.find(
        ({ finish: ringFinish }) => ringFinish === fnsh,
      );
      void _sendProductViewedEvent({
        pd: _product,
        stl: style.value as RingStyleEnum,
        fnsh,
      });

      setActive(true);
      currentRoute.pushFinish(fnsh);
    },
    [ringsByStyle, _sendProductViewedEvent, style.value, currentRoute],
  );

  useEffect(() => {
    /*
     * ensure only fires track event once on mount with product data
     * and only if not coming from the style step
     */

    const stylePageViewed = localStorage.getItem(WIZARD_PAGE_VIEWED);
    if (!product || productViewCalled || stylePageViewed === WizStepId.Style)
      return;

    productViewCalled = true;
    void _sendProductViewedEvent({
      pd: product,
      stl: style.value as RingStyleEnum,
      fnsh: finish.value as RingFinishEnum,
    });
  }, [
    _sendProductViewedEvent,
    finish.value,
    isOuraRing4Wizard,
    product,
    sendProductViewedEvent,
    style.value,
  ]);

  return {
    ...wizardContext,
    active,
    currencyCode,
    isSizesHalfDepleted,
    or4RingFinishesData,
    renderFinishes,
    selectedFinish,
    onSelect: handleSelect,
  };
};

export default useFinishStep;
